/* eslint-disable max-lines-per-function */
import {
  type AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  type ValidationErrors,
} from '@angular/forms';
import { toInitialUpperCase } from './string.util';

const handleErrorCategory = (category: string, fields: string[]): string => {
  const errorFields = fields.join(', ');

  switch (category) {
    case 'required':
      return `\n\nPlease provide a value for the following ${fields.length === 1 ? 'field' : 'fields'}:\n${errorFields}`;
    case 'email':
      return `\n\nPlease provide a valid email address for the following ${fields.length === 1 ? 'field' : 'fields'}:\n${errorFields}`;
    case 'maxlength':
      return `\n\nThe following ${fields.length === 1 ? 'field exceeds' : 'fields exceed'} the allowed number of characters:\n${errorFields}`;
    case 'minlength':
      return `\n\nThe following ${fields.length === 1 ? 'field does' : 'fields do'} not reach the required number of characters:\n${errorFields}`;
    case 'pattern':
      return `\n\nPlease provide a valid value for the following ${fields.length === 1 ? 'field' : 'fields'}:\n${errorFields}`;
    case 'sum':
      return `\n\nPlease provide a valid value for the following ${fields.length === 1 ? 'field' : 'fields'} (Sum of question weights must be 100):\n${errorFields}`;
    case 'range':
      return `\n\nPlease provide a valid value for the following ${fields.length === 1 ? 'field' : 'fields'} (Weight (%) must be between 0 and 100):\n${errorFields}`;
    case 'full':
      return '\n\nWeights need to add up to 100% in total.';
    case 'thresholdOrder':
      return '\n\nCompetitive Bidding Budget Threshold (USD) must be less than or equal to Competitive Bidding Budget Upper Threshold (USD).';
    case 'costBreakdownStructureRows':
      return `\n\nPlease complete the following Cost Breakdown Structure ${fields.length === 1 ? 'row' : 'rows'}:\n${fields.join('\n')}`;
    default:
      return `\n\nThe following ${fields.length === 1 ? 'field is' : 'fields are'} invalid:\n${errorFields}`;
  }
};

export const getFormErrors = (
  form: AbstractControl,
  parentKey?: string,
): Record<string, ValidationErrors> | ValidationErrors | null => {
  if (form instanceof FormControl) {
    return form.errors ?? null;
  }

  if (form instanceof FormGroup || form instanceof FormArray) {
    const groupErrors = form.errors;
    const formErrors: Record<string, ValidationErrors> = groupErrors
      ? { groupErrors }
      : {};

    Object.keys(form.controls).forEach((key) => {
      const element = form.get(key);

      if (!element) {
        return;
      }

      const error = getFormErrors(element, key);

      if (error !== null) {
        formErrors[parentKey?.toString().length ? `${parentKey}.${key}` : key] =
          error;
      }
    });

    return Object.keys(formErrors).length > 0 ? formErrors : null;
  }

  return null;
};

export const getErrorFieldsDetails = (
  errors: ValidationErrors | Record<string, ValidationErrors>,
  fields: { [key: string]: string },
): Record<string, string[]> => {
  if (typeof errors !== 'object' || errors === null) {
    return {};
  }

  return Object.keys(errors).reduce(
    (acc, key) => {
      Object.keys(errors[key]).forEach((error) => {
        const category = [
          'required',
          'email',
          'maxlength',
          'minlength',
          'pattern',
          'sum',
          'range',
          'full',
          'thresholdOrder',
        ].includes(error)
          ? error
          : 'other';

        const replacingKey = key.split('.').pop() ?? '';

        if (category === 'other') {
          const control = errors[key][error];
          const isGroupErrors = error === 'groupErrors';

          const errorDetails = isGroupErrors
            ? key.split('.')
            : error.split('.');

          const parentName = isGroupErrors
            ? ' '
            : ` ${fields[toInitialUpperCase(errorDetails[0]) as keyof typeof fields]} (${Number(errorDetails[1]) + 1}):`;

          const fieldDetails = getErrorFieldsDetails(
            isGroupErrors
              ? {
                  [errorDetails[1] ?? errorDetails[0]]: control,
                }
              : control,
            fields,
          );

          Object.keys(fieldDetails).forEach((field) => {
            const namesArray = fieldDetails[field];

            acc[field] = [
              ...(acc[field] || []),
              namesArray
                .map((name) => parentName + name)
                .reduce((accInternal, curr) => `${accInternal}, ${curr}`),
            ];
          });
        } else {
          const fieldName =
            fields[toInitialUpperCase(replacingKey) as keyof typeof fields];

          const requiredLength =
            category === 'maxlength'
              ? (errors[key][error]?.requiredLength ?? null)
              : null;

          acc[category] = [
            ...(acc[category] || []),
            requiredLength === null
              ? fieldName
              : `${fieldName} (max. ${requiredLength} characters)`,
          ];
        }
      });

      return acc;
    },
    {} as Record<string, string[]>,
  );
};

export const getErrorFields = (
  form: AbstractControl,
  fields: { [key: string]: string },
): Record<string, string[]> => {
  const errors = getFormErrors(form);

  if (typeof errors !== 'object' || errors === null) {
    return {};
  }

  return getErrorFieldsDetails(errors, fields);
};

type SnackbarData = {
  text: string;
};

type SnackbarOptions = {
  duration: number;
};

export const getValidationErrorConfig = (
  form: AbstractControl,
  fields: { [key: string]: string },
  additionalErrors?: { [key: string]: string[] },
): [SnackbarData, SnackbarOptions] => {
  const errors = getErrorFields(form, fields);

  const categories = Object.entries({ ...errors, ...additionalErrors }).map(
    ([key, value]) => handleErrorCategory(key, value),
  );

  return [
    {
      text: `The form contains validation errors. Please fix all errors before submitting the form.${categories.join('')}`,
    },
    {
      duration: categories.length ? 15000 : 7500,
    },
  ];
};
